import {
    addToWishlist,
    getSimpleWishlist,
    getWishlist,
    localStorage,
    mergeWishlist,
    myWishlist,
    removeFromWishlist,
    transGtmId,
} from "@jog/shared"
import { JoggWishlist, SimpleWishlist } from "@jog/types"
import { create } from "zustand"

export interface WishlistState {
    wishlistId: string | null
    wishlist: JoggWishlist | null
    simpleWishlist: SimpleWishlist | null
    pending: boolean
    actionPending: boolean
    error: null | Error
    initWishlist: () => Promise<void>
    addWishlistItem: (productId: string) => Promise<void>
    removeWishlistItem: (lineItemId: string) => Promise<void>
    mergeWishlist: () => Promise<void>
    getWishlist: () => Promise<void>
    myWishlist: () => Promise<void>
    actionOfSimpleWishlist: (fn: () => Promise<SimpleWishlist>, isAction?: boolean) => Promise<void>
    actionOfWishlist: (fn: () => Promise<JoggWishlist>) => Promise<void>
    clearError: () => void
}

export const initWishlistStore = () =>
    create<WishlistState>((set, get) => ({
        pending: false,
        actionPending: false,
        wishlistId: null,
        wishlist: null,
        simpleWishlist: null,
        error: null,
        clearError: () => set({ error: null }),
        actionOfSimpleWishlist: async (fn, isAction = false) => {
            if (isAction) {
                set({ actionPending: true, error: null })
            } else {
                set({ pending: true, error: null })
            }
            await fn()
                .then((simpleWishlist) => {
                    localStorage.setItem<string>("wishlistId", simpleWishlist.id)
                    set({ wishlistId: simpleWishlist.id, simpleWishlist, pending: false, actionPending: false })
                })
                .catch((e: { error: Error }) => {
                    set({ pending: false, error: e.error, actionPending: false })
                })
        },
        actionOfWishlist: async (fn) => {
            set({ pending: true, error: null })
            await fn()
                .then((wishlist) => {
                    localStorage.setItem<string>("wishlistId", wishlist.id)
                    const processedLineItems: JoggWishlist["lineItems"] = wishlist.lineItems.map((item) => {
                        return {
                            ...item,
                            discountedPrice: Math.round((item.discountedPrice || 0) * 100),
                            originalPrice: Math.round((item.originalPrice || 0) * 100),
                        }
                    })
                    set({
                        wishlistId: wishlist.id,
                        wishlist: {
                            ...wishlist,
                            lineItems: processedLineItems,
                        },
                        pending: false,
                    })
                })
                .catch((e: { error: Error }) => {
                    set({ pending: false, error: e.error })
                })
        },
        initWishlist: async () => {
            const wishlistId = localStorage.getItem<string>("wishlistId")
            if (!wishlistId) {
                return
            }
            await get().actionOfSimpleWishlist(() => getSimpleWishlist(wishlistId).then((res) => res.body))
        },
        addWishlistItem: async (productId: string) => {
            const id = get()?.wishlistId || ""
            await get().actionOfSimpleWishlist(() => addToWishlist(id, productId).then((res) => res.body), true)
        },
        removeWishlistItem: async (lineItemId: string) => {
            const id = get()?.wishlistId || ""
            if (!id) return
            await get().actionOfSimpleWishlist(
                () =>
                    removeFromWishlist(id, lineItemId)
                        .then((res) => res.body)
                        .then((data) => {
                            const newWishlist = get().wishlist?.lineItems.filter((item) => item.id !== lineItemId) || []
                            set({ wishlist: { id, lineItems: newWishlist } })
                            return data
                        }),
                true
            )
        },
        mergeWishlist: async () => {
            const wishlistId = localStorage.getItem<string>("wishlistId")
            if (!wishlistId) return
            await get().actionOfSimpleWishlist(() => mergeWishlist(wishlistId).then((res) => res.body))
        },
        getWishlist: async () => {
            const wishlistId = get()?.wishlistId || localStorage.getItem<string>("wishlistId") || ""
            if (!wishlistId) return
            await get().actionOfWishlist(() =>
                getWishlist(wishlistId)
                    .then((res) => res.body)
                    .then((wishlist) => {
                        const lineItems = wishlist.lineItems.map((lineItem) => {
                            const articleName =
                                lineItem.attributes?.find((attribute) => attribute.name === "articlename")?.value || ""
                            const colorCode =
                                lineItem.attributes?.find((attribute) => attribute.name === "colorcode")?.value || ""
                            return {
                                ...lineItem,
                                gtmId: transGtmId({ articleName, colorCode }),
                            }
                        })
                        return {
                            ...wishlist,
                            lineItems,
                        }
                    })
            )
        },
        myWishlist: async () => {
            await get().actionOfSimpleWishlist(() => myWishlist().then((res) => res.body))
        },
    }))
